import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Authentication uses httpOnly cookies set by the backend.
// withCredentials sends them on every request. No tokens are stored in JS-accessible storage.
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  // Bounds the worst case (a cold/slow backend) to a clear failure the UI's
  // existing loadError/retry states can show, instead of an indefinite spin.
  timeout: 20000,
});

// If the session cookie is missing/expired/stale (e.g. it points at a user
// that no longer exists after a DB reset), the backend returns 401 on every
// authenticated call. Without this, pages that fire several requests at once
// (Promise.all) never resolve their loading state and stay stuck blank
// forever. Broadcast it so AuthContext can flip `user` to false, which makes
// the existing <Protected> route guard redirect to /login on its own.
// Login/register/demo-login endpoints are excluded since a wrong-password
// 401 there is a normal form error, not a session problem.
//
// A single 401 is NOT proof the session is dead — a page fires several
// requests in parallel (Promise.all) and one can time out or hiccup on a
// slow/cold backend without the cookie actually being invalid. Confirm with
// one direct /auth/me check before forcing a logout; only act if that also
// comes back 401. `verifyingAuth` collapses simultaneous 401s (e.g. 5 of the
// 9 parallel dashboard calls failing at once) into a single check.
let verifyingAuth = false;
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    if (status === 401 && !url.includes("/auth/") && !verifyingAuth) {
      verifyingAuth = true;
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        // Session is actually fine — the original 401 was a one-off blip.
      } catch (verifyErr) {
        if (verifyErr?.response?.status === 401) {
          window.dispatchEvent(new Event("auth:unauthorized"));
        }
      } finally {
        verifyingAuth = false;
      }
    }
    return Promise.reject(err);
  }
);

export const fmtMoney = (n) =>
  "₹" + (Number(n || 0)).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export const fmtNum = (n) =>
  (Number(n || 0)).toLocaleString("en-IN");

// Universal DD-MM-YYYY formatter. Accepts ISO datetime, "YYYY-MM-DD", Date objects, or empty input.
export const fmtDate = (v) => {
  if (!v) return "—";
  // Fast path: bare "YYYY-MM-DD"
  if (typeof v === "string") {
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  }
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
};

export const formatErr = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Something went wrong";
};
